import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  type AdminLoginRequest,
  type AdminUser,
  adminLogin,
  checkAdminPermission,
  clearAdminToken,
  getAdminToken,
  getAdminUser,
  setAdminToken,
  setAdminUser,
} from '@/services/admin-auth';

interface AdminAuthContextType {
  adminUser: AdminUser | null;
  permissions: string[];
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (
    permission: string | string[],
    checkType?: 'ANY' | 'ALL',
  ) => boolean;
  isAdmin: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(
  undefined,
);

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({
  children,
}) => {
  const [adminUser, setAdminUserState] = useState<AdminUser | null>(() =>
    getAdminToken() ? getAdminUser() : null,
  );
  const [permissions, setPermissions] = useState<string[]>(
    adminUser?.permissions || [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const loginRequest: AdminLoginRequest = { username, password };
      const response = await adminLogin(loginRequest);

      // 处理响应 - umi request 可能直接返回 data 或包装在 response 中
      const result = (response as any)?.data || response;

      const user: AdminUser = {
        adminId: result.adminId,
        username: result.username,
        realName: result.realName,
        role: result.role,
        permissions: result.permissions || [],
      };

      setAdminToken(result.accessToken, result.refreshToken);
      setAdminUser(user);
      setAdminUserState(user);
      setPermissions(result.permissions || []);
    } catch (err: any) {
      const errorMessage =
        err?.data?.message || err?.message || '登录失败，请重试';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearAdminToken();
    setAdminUserState(null);
    setPermissions([]);
    setError(null);
  }, []);

  const hasPermission = useCallback(
    (permission: string | string[], checkType: 'ANY' | 'ALL' = 'ANY') => {
      return checkAdminPermission(permission, permissions, checkType);
    },
    [permissions],
  );

  const value: AdminAuthContextType = {
    adminUser,
    permissions,
    isLoading,
    error,
    login,
    logout,
    hasPermission,
    isAdmin: !!adminUser,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};
